/**
 * Formulaire d'inscription créateur — /inscription
 * Support ?next=... pour reprendre un flux checkout après inscription.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Navbar } from '@/components/site/Navbar';
import { Footer } from '@/components/site/Footer';
import { useAuth } from '@/lib/auth';
import { useT, useLang } from '@/lib/i18n';
import { track } from '@/lib/analytics';
import { setPageMeta } from '@/lib/seo';
import { toast } from 'sonner';
import { ArrowRight, Loader2 } from 'lucide-react';

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

export default function Register() {
  const t = useT();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get('next') || '/studio';
  const { user, loading, register } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setPageMeta({
      title: t('seo.register_title'),
      description: t('seo.register_description'),
      path: '/inscription',
    });
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [t, lang]);

  // Redirect si déjà connecté
  useEffect(() => {
    if (!loading && user) navigate(nextPath, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);

  const validate = () => {
    const e = {};
    const em = email.trim();
    if (!em) e.email = t('errors.email_required');
    else if (!EMAIL_RE.test(em) || em.length > 254) e.email = t('errors.email_invalid');
    if (!password) e.password = t('auth.errors.password_required');
    else if (password.length < 8) e.password = t('auth.errors.password_too_short');
    if (!acceptedTerms) e.terms = t('auth.errors.terms_required');
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;

    setSubmitting(true);
    const res = await register({
      email: email.trim().toLowerCase(),
      password,
      lang,
      accepted_terms: true,
    });
    setSubmitting(false);
    if (res.ok) {
      track('signup_completed', { lang });
      toast.success(t('auth.register_success'), { duration: 4000 });
      navigate(nextPath, { replace: true });
    } else {
      const msg = t(`errors.${res.code || 'auth_generic'}`);
      toast.error(msg, { duration: 5000 });
      if (res.code === 'auth_email_taken') setErrors({ email: msg });
      if (res.code === 'auth_terms_required') setErrors({ terms: msg });
    }
  };

  return (
    <div data-testid="page-register">
      <Navbar />
      <main>
        <section className="relative overflow-hidden pt-[128px] md:pt-[168px] pb-24">
          <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
            <div
              className="absolute left-1/2 -top-24 h-[420px] w-[420px] -translate-x-1/2 rounded-full blur-3xl"
              style={{ background: 'radial-gradient(closest-side, rgba(139,92,246,0.28), transparent 70%)' }}
            />
          </div>

          <div className="om-container max-w-md">
            <div className="rounded-[28px] border border-[var(--om-border)] bg-[var(--om-bg-pure)] p-8 shadow-[0_30px_80px_-40px_rgba(10,14,26,0.25)] md:p-10">
              <span className="om-eyebrow">{t('auth.register_eyebrow')}</span>
              <h1 className="om-h1 mt-4 text-[30px] sm:text-[36px] text-[var(--om-ink)]">
                {t('auth.register_title')}
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-[var(--om-muted)]">
                {t('auth.register_subtitle')}
              </p>

              <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                    {t('auth.email_label')}
                  </label>
                  <input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-xl border border-[var(--om-border)] bg-[var(--om-bg)] px-3.5 py-2.5 text-sm text-[var(--om-ink)] placeholder:text-[var(--om-muted-2)] focus:border-[var(--om-ink)] focus:outline-none focus:ring-2 focus:ring-[var(--om-ink)]/10"
                    placeholder="vous@openmeta.tech"
                    data-testid="register-email-input"
                  />
                  {errors.email && (
                    <div className="mt-1 text-[11px] text-rose-500" data-testid="register-email-error">{errors.email}</div>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                    {t('auth.password_label')}
                  </label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-xl border border-[var(--om-border)] bg-[var(--om-bg)] px-3.5 py-2.5 text-sm text-[var(--om-ink)] placeholder:text-[var(--om-muted-2)] focus:border-[var(--om-ink)] focus:outline-none focus:ring-2 focus:ring-[var(--om-ink)]/10"
                    placeholder={t('auth.password_placeholder')}
                    data-testid="register-password-input"
                  />
                  {errors.password && (
                    <div className="mt-1 text-[11px] text-rose-500" data-testid="register-password-error">{errors.password}</div>
                  )}
                  <div className="mt-1 text-[11px] text-[var(--om-muted-2)]">{t('auth.password_hint')}</div>
                </div>

                <div>
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-[var(--om-border)] text-[var(--om-ink)] focus:ring-2 focus:ring-[var(--om-ink)]/20"
                      data-testid="register-terms-checkbox"
                    />
                    <span className="text-[13px] leading-relaxed text-[var(--om-ink-soft)]">
                      {t('auth.terms_prefix')}{' '}
                      <Link
                        to="/conditions-utilisation"
                        target="_blank"
                        rel="noreferrer"
                        className="font-semibold text-[var(--om-ink)] underline underline-offset-4"
                        data-testid="register-terms-link-terms"
                      >
                        {t('auth.terms_label_terms')}
                      </Link>{' '}
                      {t('auth.terms_and')}{' '}
                      <Link
                        to="/confidentialite"
                        target="_blank"
                        rel="noreferrer"
                        className="font-semibold text-[var(--om-ink)] underline underline-offset-4"
                        data-testid="register-terms-link-privacy"
                      >
                        {t('auth.terms_label_privacy')}
                      </Link>
                      .
                    </span>
                  </label>
                  {errors.terms && (
                    <div className="mt-1 text-[11px] text-rose-500" data-testid="register-terms-error">{errors.terms}</div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={submitting || !acceptedTerms}
                  className="om-btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60"
                  data-testid="register-submit-btn"
                >
                  {submitting && <Loader2 size={14} strokeWidth={2.5} className="animate-spin" />}
                  {submitting ? t('auth.submitting') : t('auth.register_cta')}
                  {!submitting && <ArrowRight size={14} strokeWidth={2.4} />}
                </button>
              </form>

              <div className="mt-6 text-center text-sm text-[var(--om-muted)]">
                {t('auth.have_account')}{' '}
                <Link
                  to={`/connexion${nextPath !== '/studio' ? `?next=${encodeURIComponent(nextPath)}` : ''}`}
                  className="font-semibold text-[var(--om-ink)] underline underline-offset-4"
                  data-testid="register-goto-login"
                >
                  {t('auth.login_cta')}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
